import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Check, CreditCard, ArrowLeft, ServerCog, ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { trackPageview, trackEvent } from "@/lib/analytics";
import { WEBSITE_PACKAGES, WEBSITE_BETRIEB, WEBSITE_SUPPORT, WEBSITE_EXPRESS } from "@/config/content";
import { useSiteContent } from "@/context/SiteContent";

const fmtEur = (cents) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const EASE = [0.16, 1, 0.3, 1];

export default function WebsiteBookingPage() {
  const { content } = useSiteContent();
  const prices = content.prices || {};
  // Content-Store-Overrides (KI-Manager); Server rechnet beim Checkout selbst nach
  const packages = WEBSITE_PACKAGES.map((p) => ({ ...p, price: prices[`website_${p.id}`] ?? p.price }));
  const supports = WEBSITE_SUPPORT.map((s) => ({ ...s, price: prices[`support_${s.id}`] ?? s.price }));
  const expressPrice = prices.website_express ?? WEBSITE_EXPRESS.price;
  const betriebPrice = prices.website_betrieb ?? WEBSITE_BETRIEB.price;
  const [params] = useSearchParams();
  const [pkgId, setPkgId] = useState("plus");
  const [support, setSupport] = useState("none");
  const [express, setExpress] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Website buchen | JUSTIN HILFT";
    trackPageview("/buchen/website");
    const p = params.get("paket");
    if (p && packages.some((x) => x.id === p)) setPkgId(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pkg = packages.find((x) => x.id === pkgId);
  const supportOpt = supports.find((x) => x.id === support);
  const monthly = betriebPrice + (supportOpt?.price || 0);
  const total = pkg.price + (express ? expressPrice : 0);

  const valid = useMemo(
    () => form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email) && consent,
    [form, consent]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || loading) return;
    setLoading(true);
    trackEvent("website_kauf_gestartet", { paket: pkgId, support });
    try {
      const { data } = await api.post("/website/checkout", {
        package: pkgId,
        support_tier: support,
        express,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: form.notes.trim(),
        consent: true,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-black/10 px-4 py-3.5 text-base text-ink placeholder-zinc-400 focus:border-gold focus:outline-none transition-colors duration-300";

  return (
    <main data-testid="website-booking-page" className="min-h-screen bg-paper text-ink px-6 lg:px-12 py-10 lg:py-14">
      <div className="max-w-6xl mx-auto">
        <Link to="/" data-testid="booking-back-home" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-ink transition-colors duration-300 mb-10">
          <ArrowLeft size={14} /> Zurück zur Startseite
        </Link>

        <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-3">Website-Erstellung</p>
        <h1 className="font-display uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl mb-2">
          Deine Website <span className="font-editorial italic normal-case tracking-normal text-gold">buchen.</span>
        </h1>
        <p className="text-zinc-500 max-w-2xl mb-12">
          Paket wählen, optional Support dazu, online bezahlen — ich melde mich danach persönlich bei dir und wir
          besprechen Inhalte und Design.
        </p>

        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7 space-y-10">
            {/* 1. Paket */}
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">1 · Paket wählen</p>
              <div className="grid sm:grid-cols-3 gap-4" data-testid="package-select">
                {packages.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setPkgId(p.id)}
                    data-testid={`package-option-${p.id}`}
                    className={`relative text-left p-5 border transition-all duration-300 ${
                      pkgId === p.id
                        ? "border-gold bg-gold/5 shadow-[0_8px_32px_rgba(212,175,55,0.12)]"
                        : "border-black/10 bg-white hover:border-black/30"
                    }`}
                  >
                    {p.recommended && (
                      <span className="absolute -top-2.5 left-4 bg-gold text-ink text-[9px] font-bold uppercase tracking-[0.2em] px-2.5 py-1">
                        Empfohlen
                      </span>
                    )}
                    <p className="font-display uppercase tracking-tight text-lg">{p.name}</p>
                    <p className="font-display tracking-tight text-2xl mt-1">{fmtEur(p.price)}</p>
                    <p className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 mt-1">einmalig</p>
                    <p className="text-xs text-zinc-500 mt-2 leading-snug">{p.tagline}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* 2. Support */}
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">2 · Support wählen (optional)</p>
              <div className="grid sm:grid-cols-3 gap-4" data-testid="support-select">
                <button
                  type="button"
                  onClick={() => setSupport("none")}
                  data-testid="support-option-none"
                  className={`text-left p-5 border transition-all duration-300 ${
                    support === "none" ? "border-gold bg-gold/5" : "border-black/10 bg-white hover:border-black/30"
                  }`}
                >
                  <p className="font-display uppercase tracking-tight text-lg">Ohne Support</p>
                  <p className="text-xs text-zinc-500 mt-2 leading-snug">Du kümmerst dich selbst um deine Website.</p>
                </button>
                {supports.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => setSupport(s.id)}
                    data-testid={`support-option-${s.id}`}
                    className={`text-left p-5 border transition-all duration-300 ${
                      support === s.id ? "border-gold bg-gold/5" : "border-black/10 bg-white hover:border-black/30"
                    }`}
                  >
                    <p className="font-display uppercase tracking-tight text-lg">{s.name}</p>
                    <p className="font-display tracking-tight text-xl mt-1">+{fmtEur(s.price)}<span className="text-xs text-zinc-500"> / Monat</span></p>
                    <p className="text-xs text-zinc-500 mt-2 leading-snug">{s.tagline}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* 3. Express */}
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">3 · Express-Lieferung (optional)</p>
              <button
                type="button"
                onClick={() => setExpress((v) => !v)}
                data-testid="express-toggle"
                aria-pressed={express}
                className={`w-full text-left p-5 border-2 transition-all duration-300 flex items-center gap-4 ${
                  express
                    ? "border-gold bg-gold/5 shadow-[0_8px_32px_rgba(212,175,55,0.15)]"
                    : "border-black/10 bg-white hover:border-gold/50"
                }`}
              >
                <span className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center transition-colors duration-300 ${express ? "bg-gold text-ink" : "bg-black/5 text-zinc-400"}`}>
                  <Zap size={20} />
                </span>
                <span className="flex-1">
                  <span className="font-display uppercase tracking-tight text-lg block">
                    {WEBSITE_EXPRESS.label} — +{fmtEur(expressPrice)}
                  </span>
                  <span className="text-xs text-zinc-500 leading-snug block mt-0.5">
                    {WEBSITE_EXPRESS.tagline} (ab Zahlungseingang)
                  </span>
                </span>
                <span className={`w-6 h-6 shrink-0 border-2 flex items-center justify-center transition-colors duration-300 ${express ? "border-gold bg-gold" : "border-black/20"}`}>
                  {express && <Check size={14} className="text-ink" />}
                </span>
              </button>
            </div>

            {/* 4. Daten */}
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">4 · Deine Daten</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Dein Name *"
                  data-testid="booking-name-input"
                  className={inputCls}
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="E-Mail *"
                  data-testid="booking-email-input"
                  className={inputCls}
                />
                <input
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="Telefon (optional)"
                  data-testid="booking-phone-input"
                  className={inputCls}
                />
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Worum geht es bei deinem Projekt? (optional)"
                  rows={3}
                  data-testid="booking-notes-input"
                  className={`${inputCls} sm:col-span-2 resize-none`}
                />
              </div>
            </div>
          </div>

          {/* Zusammenfassung */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="border border-black/10 bg-white p-7 lg:sticky lg:top-24"
              data-testid="booking-summary"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6">Zusammenfassung</p>
              <dl className="space-y-4 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-zinc-600">Website {pkg.name} (einmalig)</dt>
                  <dd className="font-medium" data-testid="summary-package-price">{fmtEur(pkg.price)}</dd>
                </div>
                {express && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-zinc-600 flex items-center gap-2">
                      <Zap size={14} className="text-gold" /> Express-Lieferung (48 h)
                    </dt>
                    <dd className="font-medium" data-testid="summary-express-price">+{fmtEur(expressPrice)}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-4 items-baseline">
                  <dt className="text-zinc-600">Heute zu zahlen</dt>
                  <dd className="font-display text-2xl" data-testid="summary-total">{fmtEur(total)}</dd>
                </div>
                <div className="border-t border-black/5 pt-4 flex justify-between gap-4">
                  <dt className="text-zinc-600 flex items-center gap-2">
                    <ServerCog size={14} className="text-gold" /> Website-Betrieb
                  </dt>
                  <dd className="font-medium">{fmtEur(betriebPrice)} / Monat</dd>
                </div>
                {supportOpt && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-zinc-600 flex items-center gap-2">
                      <ShieldCheck size={14} className="text-gold" /> {supportOpt.name}
                    </dt>
                    <dd className="font-medium" data-testid="summary-support-price">+{fmtEur(supportOpt.price)} / Monat</dd>
                  </div>
                )}
                <div className="border-t border-black/10 pt-4 flex justify-between gap-4 items-baseline">
                  <dt className="text-zinc-600">Monatlich ab Fertigstellung</dt>
                  <dd className="font-display text-2xl" data-testid="summary-monthly">{fmtEur(monthly)}</dd>
                </div>
              </dl>

              <p className="text-xs text-zinc-500 leading-relaxed mt-5">
                Die monatliche Abbuchung startet erst, wenn deine Website fertig ist und online geht — nicht heute.
              </p>

              <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="booking-consent-label">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="booking-consent-checkbox"
                  className="mt-1 w-4 h-4 accent-[#d4af37]"
                />
                <span className="text-xs text-zinc-600 leading-relaxed">
                  Ich stimme zu, dass ab Fertigstellung meiner Website die monatliche Gebühr von{" "}
                  <strong>{fmtEur(monthly)}</strong> automatisch über meine hinterlegte Karte abgebucht wird. Hinweise
                  dazu stehen in den <Link to="/agb" className="underline hover:text-gold">AGB</Link> (Punkt 9).
                </span>
              </label>

              <button
                type="submit"
                disabled={!valid || loading}
                data-testid="booking-checkout-button"
                className="group relative overflow-hidden w-full bg-gold text-ink py-4 mt-6 text-xs uppercase tracking-[0.3em] font-medium shadow-[0_0_24px_rgba(212,175,55,0.35)] hover:shadow-[0_0_36px_rgba(212,175,55,0.55)] transition-shadow duration-500 disabled:opacity-50 disabled:shadow-none"
              >
                <span className="relative flex items-center justify-center gap-3">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  {loading ? "Weiter zur Zahlung ..." : `Jetzt kaufen — ${fmtEur(total)}`}
                </span>
              </button>
              <p className="text-[11px] text-zinc-400 text-center mt-3">
                Sichere Zahlung mit Kredit-/Debitkarte über Stripe. Deine Karte wird für die spätere monatliche
                Abbuchung gespeichert. Gemäß § 19 UStG keine Umsatzsteuer.
              </p>
            </motion.div>
          </div>
        </form>
      </div>
    </main>
  );
}
